import { useState, useEffect, useMemo } from "react"
import { useRegisterActions, Action } from "kbar"
import { loadRecentDocs, saveToRecentHistory } from "../utils/storage"
import {
  STORAGE_KEY,
  UPDATE_EVENT_NAME,
  RECENT_PRIORITY_BASE,
} from "../constants"
import { useCurrentRepo } from "../hooks/useCurrentRepo"
import type { RecentDocsByRepo } from "../types"

export function RecentlyViewedDocs() {
  const currentRepo = useCurrentRepo()

  // Don't render at all if we're outside docs context
  if (!currentRepo) {
    return null
  }

  const [recentByRepo, setRecentByRepo] =
    useState<RecentDocsByRepo>(loadRecentDocs)

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const updated = JSON.parse(e.newValue)
          setRecentByRepo(updated)
        } catch (error) {
          console.error("Failed to parse recent docs:", error)
        }
      }
    }

    const handleLocalUpdate = (e: CustomEvent) => {
      if (e.detail?.recentByRepo) {
        setRecentByRepo(e.detail.recentByRepo)
      }
    }

    window.addEventListener("storage", handleStorageChange)
    window.addEventListener(
      UPDATE_EVENT_NAME,
      handleLocalUpdate as EventListener
    )

    return () => {
      window.removeEventListener("storage", handleStorageChange)
      window.removeEventListener(
        UPDATE_EVENT_NAME,
        handleLocalUpdate as EventListener
      )
    }
  }, [])

  const pathname =
    typeof window !== "undefined" ? window.location.pathname : null

  const recentDocs = useMemo(
    () => recentByRepo[currentRepo] || [],
    [recentByRepo, currentRepo]
  )

  const currentPathMap = useMemo(() => {
    const map = new Map<string, boolean>()
    recentDocs.forEach((doc) => {
      const isCurrent =
        typeof window !== "undefined" && doc.path === window.location.pathname
      map.set(`recent-${doc.path}`, isCurrent)
    })
    return map
  }, [recentDocs, pathname])

  const actions: Action[] = useMemo(
    () =>
      recentDocs.map((doc, index) => {
        const priority = RECENT_PRIORITY_BASE - index

        return {
          id: `recent-${doc.path}`,
          name: doc.title,
          subtitle: doc.path,
          section: "Recently viewed",
          keywords: doc.title,
          priority,
          perform: () => {
            if (typeof window !== "undefined") {
              saveToRecentHistory(doc.path, doc.title)
              window.location.href = doc.path
            }
          },
        }
      }),
    [recentDocs]
  )

  useRegisterActions(actions, [actions, currentPathMap])
  return null
}
